import { create } from 'zustand';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  timestamp: number;
  read: boolean;
  source?: string; // e.g. 'compile', 'deploy', 'ai', 'git', 'system'
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;

  addNotification: (n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

/* ================================================================== */
/*  Store                                                             */
/* ================================================================== */

let nextId = 1;

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,

  addNotification: (n) => {
    const notification: Notification = {
      ...n,
      id: `notif-${nextId++}`,
      timestamp: Date.now(),
      read: false,
    };

    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 100), // Keep max 100
      unreadCount: state.unreadCount + 1,
    }));
  },

  markRead: (id) => {
    set((state) => {
      const found = state.notifications.find((n) => n.id === id);
      if (!found || found.read) return state;
      return {
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  removeNotification: (id) => {
    set((state) => {
      const found = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: found && !found.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  clearAll: () => set({ notifications: [], unreadCount: 0 }),
}));

/* ================================================================== */
/*  Helper: quick-add functions for common notification types         */
/* ================================================================== */

export function notifyCompileSuccess(contractName?: string) {
  useNotificationStore.getState().addNotification({
    type: 'success',
    title: 'Compilation Successful',
    message: contractName
      ? `${contractName} compiled without errors`
      : 'Contract compiled successfully',
    source: 'compile',
  });
}

export function notifyCompileError(errorMsg?: string) {
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Compilation Failed',
    message: errorMsg ?? 'Check the output for details',
    source: 'compile',
  });
}

export function notifyTestSuccess(passed: number, failed: number, skipped: number) {
  useNotificationStore.getState().addNotification({
    type: 'success',
    title: 'Tests Passed',
    message: `${passed} passed${failed > 0 ? `, ${failed} failed` : ''}${skipped > 0 ? `, ${skipped} skipped` : ''}`,
    source: 'test',
  });
}

export function notifyTestError(
  message?: string,
  passed?: number,
  failed?: number,
  skipped?: number,
) {
  const summary = [
    typeof passed === 'number' ? `${passed} passed` : null,
    typeof failed === 'number' ? `${failed} failed` : null,
    typeof skipped === 'number' && skipped > 0 ? `${skipped} skipped` : null,
  ]
    .filter(Boolean)
    .join(', ');

  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Tests Failed',
    message: (message ?? summary) || 'Check the output for details',
    source: 'test',
  });
}

export function notifyDeploySuccess(network: string, address?: string) {
  useNotificationStore.getState().addNotification({
    type: 'success',
    title: 'Deployment Successful',
    message: address ? `Deployed to ${network} at ${address}` : `Deployed to ${network}`,
    source: 'deploy',
  });
}

export function notifyDeployError(errorMsg?: string) {
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Deployment Failed',
    message: errorMsg ?? 'Check the output for details',
    source: 'deploy',
  });
}

export function notifyAiComplete(task: string) {
  useNotificationStore.getState().addNotification({
    type: 'info',
    title: 'AI Task Complete',
    message: task,
    source: 'ai',
  });
}

export function notifyAiAction(title: string, message?: string, type: NotificationType = 'info') {
  useNotificationStore.getState().addNotification({
    type,
    title,
    message,
    source: 'ai',
  });
}

export function notifyGitCommit(message: string, hash?: string) {
  useNotificationStore.getState().addNotification({
    type: 'success',
    title: 'Committed',
    message: hash ? `${message} (${hash.slice(0, 7)})` : message,
    source: 'git',
  });
}

export function notifyError(title: string, message?: string) {
  useNotificationStore.getState().addNotification({
    type: 'error',
    title,
    message,
    source: 'system',
  });
}
